import { useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { TopHeader } from './TopHeader';
import { useStationMode } from '@/hooks/use-role';
import { PageTitleProvider } from '@/contexts/PageTitleContext';
import { HeaderSlotProvider } from '@/contexts/HeaderSlotContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSetupMode } from '@/features/onboarding/hooks/useSetupMode';
import { SETUP_BAR_HEIGHT, TOP_HEADER_HEIGHT, isOnboardingPath } from '@/features/onboarding/lib/setup-mode';
import { BackToSetupBar } from '@/features/onboarding/components/BackToSetupBar';
import { WhatsNewDialog } from '@/features/help/changelog/WhatsNewDialog';

/** Today's style object, byte-identical for every org that is not in setup mode. */
const DEFAULT_SHELL_STYLE = { ['--top-header-height' as string]: TOP_HEADER_HEIGHT } as React.CSSProperties;

/**
 * With the Back to setup bar showing, its height is folded into
 * `--top-header-height` (rather than a var only PageActionBar reads), because
 * on the setup-mode pages three sticky surfaces read that var: PageActionBar,
 * the inventory sticky table head and the shared-location banner.
 */
const SETUP_BAR_SHELL_STYLE = {
  ['--setup-bar-height' as string]: SETUP_BAR_HEIGHT,
  ['--top-header-height' as string]: `calc(${TOP_HEADER_HEIGHT} + var(--setup-bar-height))`,
} as React.CSSProperties;

export function AppLayout() {
  // Station users with tracker.view get a filtered sidebar (useStationMode).
  const stationMode = useStationMode();
  // Setup mode (BLU-1376): no sidebar, and a Back to setup bar off the wizard.
  const inSetupMode = useSetupMode();
  const { pathname } = useLocation();
  const showSetupBar = inSetupMode && !isOnboardingPath(pathname);

  // Sidebar reveal on finish (D7): remember the org last seen in setup mode.
  // When that same org leaves setup mode in-session, the sidebar mounts with a
  // fade, and `reveal` stays true on later renders (the sidebar is already
  // mounted, so navigation does not re-run the animation). A cold load of a
  // live org, a switch to another org, or the null org mid-switch never
  // animates. A render-time ref write: no state, no timer, no effect.
  const { organization } = useOrganization();
  const setupOrgRef = useRef<string | null>(null);
  const orgId = organization?.id ?? null;
  if (inSetupMode && orgId) setupOrgRef.current = orgId;
  const reveal = !inSetupMode && !!orgId && setupOrgRef.current === orgId;

  return (
    <PageTitleProvider>
      <HeaderSlotProvider>
        <SidebarProvider style={showSetupBar ? SETUP_BAR_SHELL_STYLE : DEFAULT_SHELL_STYLE}>
          {!stationMode && !inSetupMode && <AppSidebar reveal={reveal} />}
          <SidebarInset>
            <TopHeader stationMode={stationMode} setupMode={inSetupMode} />
            {showSetupBar && <BackToSetupBar />}
            <main className="flex-1 flex flex-col">
              <Outlet />
            </main>
            {/* Newest changelog highlights, coordinators and above only. */}
            {!stationMode && !inSetupMode && <WhatsNewDialog />}
          </SidebarInset>
        </SidebarProvider>
      </HeaderSlotProvider>
    </PageTitleProvider>
  );
}
